import React, { useState, useEffect, useRef, useCallback } from 'react'
import Button from '../../components/common/Button'
import { toast } from '../../components/toast/CustomToast'
import api from '../../utils/api'

const getToken = () => localStorage.getItem('access_token')
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` })

// ── CSV Utilities ───────────────────────────────────────────────────────────────

function detectDelimiter(line) {
    const semicolons = (line.match(/;/g) || []).length
    const commas = (line.match(/,/g) || []).length
    return semicolons > commas ? ';' : ','
}

function parseRow(line, delimiter) {
    const result = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') { current += '"'; i++ }
            else inQuotes = !inQuotes
        } else if (ch === delimiter && !inQuotes) {
            result.push(current.trim())
            current = ''
        } else {
            current += ch
        }
    }
    result.push(current.trim())
    return result
}

function parseCSV(text) {
    const clean = text.replace(/^\uFEFF/, '') // strip BOM
    const lines = clean.split(/\r?\n/).filter(l => l.trim().length > 0)
    if (lines.length < 2) return { headers: [], rows: [], delimiter: ',' }
    const delimiter = detectDelimiter(lines[0])
    const headers = parseRow(lines[0], delimiter)
    const rows = lines.slice(1).map(l => parseRow(l, delimiter))
    return { headers, rows, delimiter }
}

function validateCSVContent(rawText, headers, rows) {
    // Binary content — null bytes indicate a non-text file
    if (rawText.includes('\0')) {
        return 'O arquivo parece ser um arquivo binário, não um CSV de texto.'
    }

    // High density of non-printable characters (e.g. Word .doc, .pdf renamed to .csv)
    const sample = rawText.slice(0, 2000)
    const nonPrintable = (sample.match(/[\x01-\x08\x0E-\x1F\x7F]/g) || []).length
    if (nonPrintable / sample.length > 0.02) {
        return 'O arquivo contém caracteres inválidos. Verifique se é realmente um CSV de texto.'
    }

    // Must produce at least 2 columns — otherwise no delimiter was found
    if (headers.length < 2) {
        return 'Nenhum separador de colunas foi detectado (vírgula ou ponto-e-vírgula). Verifique o formato do arquivo.'
    }

    // Headers shouldn't be very long — a single long line suggests it's prose, not a CSV header
    if (headers.some(h => h.length > 200)) {
        return 'Os cabeçalhos do arquivo são muito longos. Verifique se a primeira linha contém os nomes das colunas.'
    }

    // Must have at least one data row
    if (rows.length === 0) {
        return 'O arquivo não contém registros além do cabeçalho.'
    }

    // Row consistency: most rows should have the same column count as the header
    const expected = headers.length
    const inconsistent = rows.filter(r => Math.abs(r.length - expected) > expected * 0.5).length
    if (rows.length > 1 && inconsistent / rows.length > 0.5) {
        return 'A maioria das linhas tem um número de colunas muito diferente do cabeçalho. Verifique o delimitador do arquivo.'
    }

    return null // valid
}

// ── Auto-detection: exact label match only ──────────────────────────────────────

function autoDetect(fieldLabel, headers) {
    const norm = (s) => s.toLowerCase().trim()
    return headers.find(h => norm(h) === norm(fieldLabel)) || ''
}

// Apply saved mapping — only keep columns that still exist in the current CSV headers
function applySavedMapping(savedMapping, headers) {
    const result = {}
    for (const [key, col] of Object.entries(savedMapping || {})) {
        result[key] = headers.includes(col) ? col : ''
    }
    return result
}

// ── Sub-components ──────────────────────────────────────────────────────────────

const Toggle = ({ checked, onChange, disabled = false }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        title={disabled ? 'Campo chave — sempre utilizado para identificação' : checked ? 'Atualizar se existir' : 'Não atualizar se existir'}
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${
            disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
        } ${checked ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
        <span
            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                checked ? 'translate-x-4' : 'translate-x-0'
            }`}
        />
    </button>
)

const DropZone = ({ onFile, file, rowCount }) => {
    const inputRef = useRef(null)
    const [dragging, setDragging] = useState(false)

    const validate = (f) => {
        if (!f) return
        if (!f.name.toLowerCase().endsWith('.csv')) {
            toast.error(`Arquivo inválido: "${f.name}". Somente arquivos .csv são aceitos.`)
            return
        }
        onFile(f)
    }

    const handleDrop = useCallback((e) => {
        e.preventDefault()
        setDragging(false)
        validate(e.dataTransfer.files[0])
    }, [onFile])

    const handleChange = (e) => {
        validate(e.target.files[0])
        e.target.value = ''
    }

    if (file) {
        return (
            <div className="flex items-center gap-4 p-4 rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40 shrink-0">
                    <span className="material-icons text-green-600 dark:text-green-400" style={{ fontSize: 22 }}>description</span>
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {rowCount} {rowCount === 1 ? 'registro encontrado' : 'registros encontrados'}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 font-medium transition shrink-0"
                >
                    <span className="material-icons" style={{ fontSize: 14 }}>swap_horiz</span>
                    Trocar arquivo
                </button>
                <input ref={inputRef} type="file" accept=".csv" onChange={handleChange} className="hidden" />
            </div>
        )
    }

    return (
        <div
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
                dragging
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700/30'
            }`}
        >
            <div className={`w-12 h-12 flex items-center justify-center rounded-full transition-colors ${
                dragging ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-700'
            }`}>
                <span className={`material-icons transition-colors ${dragging ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}`} style={{ fontSize: 26 }}>
                    upload_file
                </span>
            </div>
            <div className="text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Arraste seu arquivo CSV aqui
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    ou <span className="text-blue-500 dark:text-blue-400 underline">clique para selecionar</span> — somente .csv
                </p>
            </div>
            <input ref={inputRef} type="file" accept=".csv" onChange={handleChange} className="hidden" />
        </div>
    )
}

// ── Main Modal ──────────────────────────────────────────────────────────────────

const CsvUploadModal = ({ fields, entityLabel, entityType, apiPath, onClose, onSuccess }) => {
    const [file, setFile] = useState(null)
    const [csvHeaders, setCsvHeaders] = useState([])
    const [csvRows, setCsvRows] = useState([])
    const [mapping, setMapping] = useState({})      // fieldKey → csvHeader
    const [updateFlags, setUpdateFlags] = useState({}) // fieldKey → bool
    const [processing, setProcessing] = useState(false)
    const [progress, setProgress] = useState({ done: 0, total: 0 })
    const [result, setResult] = useState(null)
    const [savedMapping, setSavedMapping] = useState(null) // last persisted state

    // Load saved mapping on mount
    useEffect(() => {
        api.get(`/company/csv-mappings/${entityType}`, { headers: authHeader() })
            .then(res => setSavedMapping(res.data))
            .catch(() => {}) // 404 = no saved mapping yet, that's fine
    }, [entityType])

    const handleFile = useCallback((f) => {
        const reader = new FileReader()
        reader.onload = (e) => {
            const rawText = e.target.result
            const { headers, rows } = parseCSV(rawText)

            const validationError = validateCSVContent(rawText, headers, rows)
            if (validationError) {
                toast.error(`Arquivo inválido: ${validationError}`)
                return
            }

            setCsvHeaders(headers)
            setCsvRows(rows)

            // Priority: saved mapping (filtered to existing headers) → label exact match → empty
            const baseMapping = savedMapping
                ? applySavedMapping(savedMapping.mapping, headers)
                : {}
            const baseFlags = savedMapping ? { ...savedMapping.update_flags } : {}

            const resolvedMapping = {}
            const resolvedFlags = {}
            fields.forEach(field => {
                resolvedMapping[field.key] = baseMapping[field.key] !== undefined
                    ? baseMapping[field.key]
                    : autoDetect(field.label, headers)
                resolvedFlags[field.key] = baseFlags[field.key] !== undefined
                    ? baseFlags[field.key]
                    : field.key !== 'code'
            })
            setMapping(resolvedMapping)
            setUpdateFlags(resolvedFlags)
            setFile(f)
            setResult(null)
        }
        reader.readAsText(f, 'UTF-8')
    }, [fields, savedMapping])

    const setMap = (fieldKey, csvCol) =>
        setMapping(prev => ({ ...prev, [fieldKey]: csvCol }))

    const setFlag = (fieldKey, val) =>
        setUpdateFlags(prev => ({ ...prev, [fieldKey]: val }))

    const mappedRowCount = csvRows.filter(row => {
        const codeCol = mapping['code']
        if (!codeCol) return false
        const idx = csvHeaders.indexOf(codeCol)
        return idx >= 0 && row[idx]?.trim()
    }).length

    const codeIsMapped = !!mapping['code']

    // At least one non-code field must be mapped AND have update enabled;
    // otherwise existing records would have nothing to update (only inserts could work).
    const hasUpdateableFields = fields.some(
        f => f.key !== 'code' && mapping[f.key] && updateFlags[f.key]
    )

    const buildRecords = () => {
        const codeIdx = csvHeaders.indexOf(mapping['code'])
        return csvRows
            .filter(row => row[codeIdx]?.trim())
            .map(row => {
                const rec = {}
                fields.forEach(field => {
                    const col = mapping[field.key]
                    if (!col) return
                    const idx = csvHeaders.indexOf(col)
                    if (idx >= 0) rec[field.key] = row[idx]?.trim() || null
                })
                return rec
            })
    }

    const BATCH_SIZE = 100

    const handleProcess = async () => {
        if (!codeIsMapped) return
        const records = buildRecords()
        const update_fields = fields
            .filter(f => f.key !== 'code' && updateFlags[f.key] && mapping[f.key])
            .map(f => f.key)

        const batches = []
        for (let i = 0; i < records.length; i += BATCH_SIZE) {
            batches.push(records.slice(i, i + BATCH_SIZE))
        }

        setProcessing(true)
        setProgress({ done: 0, total: records.length })

        const accumulated = { inserted: 0, updated: 0, skipped: 0, errors: [] }

        try {
            for (const batch of batches) {
                const res = await api.post(
                    `${apiPath}/bulk`,
                    { records: batch, update_fields },
                    { headers: authHeader() }
                )
                accumulated.inserted += res.data.inserted
                accumulated.updated  += res.data.updated
                accumulated.skipped  += res.data.skipped
                accumulated.errors.push(...(res.data.errors || []))
                setProgress(prev => ({ ...prev, done: prev.done + batch.length }))
            }
            setResult(accumulated)
            // Persist mapping for future imports (fire and forget)
            api.put(
                `/company/csv-mappings/${entityType}`,
                { entity_type: entityType, mapping, update_flags: updateFlags },
                { headers: authHeader() }
            ).catch(() => {})
            onSuccess()
        } catch (err) {
            setResult({
                ...accumulated,
                errors: [...accumulated.errors, err.response?.data?.detail || err.message || 'Erro desconhecido']
            })
        } finally {
            setProcessing(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30">
                            <span className="material-icons text-blue-500" style={{ fontSize: 18 }}>upload</span>
                        </div>
                        <div>
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                                Importar {entityLabel}s via CSV
                            </h2>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                Insere novos registros e atualiza existentes pelo Código
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={processing}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <span className="material-icons" style={{ fontSize: 20 }}>close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

                    {/* Drop zone */}
                    <DropZone onFile={handleFile} file={file} rowCount={csvRows.length} />

                    {/* Mapping section */}
                    {file && !result && (
                        <>
                            <div className="flex items-center gap-3">
                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                                    Associação de colunas
                                </span>
                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                            </div>

                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                {/* Table header */}
                                <div className="grid grid-cols-[1fr_1fr_auto] gap-0 bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                        Campo
                                    </span>
                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                        Coluna do CSV
                                    </span>
                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide text-center w-28">
                                        Atualizar se existir
                                    </span>
                                </div>

                                {/* Rows */}
                                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {fields.map(field => {
                                        const isKey = field.key === 'code'
                                        const mapped = !!mapping[field.key]
                                        return (
                                            <div
                                                key={field.key}
                                                className="grid grid-cols-[1fr_1fr_auto] gap-0 px-4 py-2.5 items-center hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition"
                                            >
                                                {/* Field label */}
                                                <div className="flex items-center gap-2 pr-3">
                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        {field.label}
                                                    </span>
                                                    {isKey && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 font-semibold uppercase tracking-wide">
                                                            chave
                                                        </span>
                                                    )}
                                                    {field.required && !isKey && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 font-semibold uppercase tracking-wide">
                                                            obrigatório
                                                        </span>
                                                    )}
                                                </div>

                                                {/* CSV column selector */}
                                                <div className="pr-4">
                                                    <select
                                                        value={mapping[field.key] || ''}
                                                        onChange={e => setMap(field.key, e.target.value)}
                                                        className={`w-full text-sm px-3 py-1.5 rounded-lg border bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                                                            isKey && !mapped
                                                                ? 'border-red-400 dark:border-red-500'
                                                                : mapped
                                                                    ? 'border-green-400 dark:border-green-600'
                                                                    : 'border-gray-300 dark:border-gray-600'
                                                        }`}
                                                    >
                                                        <option value="">— Não mapear —</option>
                                                        {csvHeaders.map(h => (
                                                            <option key={h} value={h}>{h}</option>
                                                        ))}
                                                    </select>
                                                </div>

                                                {/* Update toggle */}
                                                <div className="flex items-center justify-center w-28">
                                                    <Toggle
                                                        checked={isKey ? false : (updateFlags[field.key] ?? true)}
                                                        onChange={val => setFlag(field.key, val)}
                                                        disabled={isKey || !mapped}
                                                    />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Validation / preview */}
                            <div className="flex flex-col gap-2">
                                {!codeIsMapped ? (
                                    <div className="flex items-center gap-2 text-xs text-red-500 dark:text-red-400">
                                        <span className="material-icons" style={{ fontSize: 14 }}>error_outline</span>
                                        O campo <strong>Código</strong> é obrigatório e deve ser mapeado para uma coluna do CSV
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                                        <span className="material-icons" style={{ fontSize: 14 }}>check_circle_outline</span>
                                        <strong>{mappedRowCount}</strong>&nbsp;
                                        {mappedRowCount === 1 ? 'registro pronto' : 'registros prontos'} para processamento
                                    </div>
                                )}
                            </div>

                            {/* Data preview */}
                            {codeIsMapped && mappedRowCount > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                                        Pré-visualização (primeiros 3 registros)
                                    </p>
                                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="bg-gray-50 dark:bg-gray-700/50">
                                                    {fields.filter(f => mapping[f.key]).map(f => (
                                                        <th key={f.key} className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-semibold whitespace-nowrap">
                                                            {f.label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                {buildRecords().slice(0, 3).map((rec, i) => (
                                                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/20">
                                                        {fields.filter(f => mapping[f.key]).map(f => (
                                                            <td key={f.key} className="px-3 py-1.5 text-gray-700 dark:text-gray-300 max-w-[140px] truncate" title={rec[f.key] || ''}>
                                                                {rec[f.key] || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Result */}
                    {result && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                                <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                                    Resultado
                                </span>
                                <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div className="flex flex-col items-center gap-1 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                                    <span className="text-2xl font-black text-green-600 dark:text-green-400">{result.inserted}</span>
                                    <span className="text-xs font-medium text-green-700 dark:text-green-400">Inseridos</span>
                                </div>
                                <div className="flex flex-col items-center gap-1 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                    <span className="text-2xl font-black text-blue-600 dark:text-blue-400">{result.updated}</span>
                                    <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Atualizados</span>
                                </div>
                                <div className="flex flex-col items-center gap-1 p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                                    <span className="text-2xl font-black text-gray-500 dark:text-gray-400">{result.skipped}</span>
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Ignorados</span>
                                </div>
                            </div>

                            {result.errors.length > 0 && (
                                <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                                    <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-2 flex items-center gap-1.5">
                                        <span className="material-icons" style={{ fontSize: 14 }}>warning</span>
                                        {result.errors.length} {result.errors.length === 1 ? 'erro encontrado' : 'erros encontrados'}
                                    </p>
                                    <ul className="space-y-1 max-h-32 overflow-y-auto">
                                        {result.errors.map((e, i) => (
                                            <li key={i} className="text-xs text-red-700 dark:text-red-300 font-mono">{e}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {result.errors.length === 0 && (
                                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                                    <span className="material-icons" style={{ fontSize: 18 }}>check_circle</span>
                                    Importação concluída sem erros!
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex flex-col gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">

                    {/* Progress bar — visible only while processing */}
                    {processing && progress.total > 0 && (
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1.5">
                                    <span className="material-icons animate-spin text-blue-500" style={{ fontSize: 13 }}>sync</span>
                                    Processando registros...
                                </span>
                                <span className="font-semibold tabular-nums">
                                    {Math.min(progress.done, progress.total)} / {progress.total}
                                </span>
                            </div>
                            <div className="w-full h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-300 ease-out"
                                    style={{ width: `${progress.total > 0 ? Math.round((Math.min(progress.done, progress.total) / progress.total) * 100) : 0}%` }}
                                />
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 text-right">
                                {progress.total > 0 ? Math.round((Math.min(progress.done, progress.total) / progress.total) * 100) : 0}% concluído
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-between">
                        {!result ? (
                            <>
                                <div className="text-xs text-gray-400 dark:text-gray-500">
                                    {file
                                        ? `${csvHeaders.length} coluna${csvHeaders.length !== 1 ? 's' : ''} detectada${csvHeaders.length !== 1 ? 's' : ''} no arquivo`
                                        : 'Selecione um arquivo .csv para continuar'}
                                </div>
                                <div className="flex items-center gap-3">
                                    <Button variant="secondary" onClick={onClose} disabled={processing}>
                                        Cancelar
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleProcess}
                                        disabled={!file || !codeIsMapped || mappedRowCount === 0 || processing}
                                    >
                                        {processing ? (
                                            <>
                                                <span className="material-icons animate-spin" style={{ fontSize: 16 }}>sync</span>
                                                Processando...
                                            </>
                                        ) : (
                                            <>
                                                <span className="material-icons" style={{ fontSize: 16 }}>cloud_upload</span>
                                                Processar {mappedRowCount > 0 ? `${mappedRowCount} registros` : ''}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="flex justify-end w-full">
                                <Button variant="primary" onClick={onClose}>
                                    <span className="material-icons" style={{ fontSize: 16 }}>check</span>
                                    Concluir
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CsvUploadModal
